import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ExternalLink, Loader2, Send, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { CharCounter } from '@/components/ui/char-counter';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import supportTicketService, {
  SUPPORT_TICKET_CHANNEL_LABELS,
  type SupportTicketConfig,
  type SupportTicketDetail,
} from '@/services/supportTicketService';
import { SupportTicketStatusBadge } from './SupportTicketStatusBadge';
import { formatDateTime, formatEur } from './format';
import { markTicketSeen } from './seen';

function billingNote(ticket: SupportTicketDetail): string {
  if (ticket.status === 'withdrawn') return 'Zurückgezogen – es wird nichts berechnet.';
  if (ticket.status === 'resolved') {
    return ticket.billable
      ? `Berechnet: ${formatEur(ticket.price_eur)} netto pauschal, auf deiner nächsten Rechnung.`
      : 'Die Bearbeitung ist für dich kostenlos.';
  }
  return `${formatEur(ticket.price_eur)} netto pauschal – berechnet erst, wenn das Problem gelöst ist.`;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground break-words">{children}</p>
    </div>
  );
}

/** Ein Ticket aus Sicht des Salons: Angaben, Verlauf mit unserem Team, Antwort. */
export function SupportTicketView({
  ticketId,
  config,
  onBack,
  onChanged,
}: {
  ticketId: number;
  config: SupportTicketConfig;
  onBack: () => void;
  onChanged: (ticket: SupportTicketDetail) => void;
}) {
  const [ticket, setTicket] = useState<SupportTicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const headingRef = useRef<HTMLDivElement>(null);
  const focusedOnce = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setTicket(await supportTicketService.get(ticketId));
    } catch (e: any) {
      setFailed(true);
      toast.error(e?.message || 'Ticket konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = (next: SupportTicketDetail) => {
    setTicket(next);
    onChanged(next);
  };

  // Geöffnet = gelesen: „Neue Antwort" in der Liste verschwindet.
  useEffect(() => {
    if (ticket) markTicketSeen(ticket.id, ticket.last_message_at);
  }, [ticket]);

  // Ansicht gewechselt: Fokus einmal auf die Überschrift, nicht auf <body>.
  useEffect(() => {
    if (ticket && !focusedOnce.current) {
      focusedOnce.current = true;
      headingRef.current?.focus();
    }
  }, [ticket]);

  const sendReply = async () => {
    const body = reply.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      apply(await supportTicketService.reply(ticketId, body));
      setReply('');
      toast.success('Nachricht gesendet.');
    } catch (e: any) {
      toast.error(e?.message || 'Nachricht konnte nicht gesendet werden.');
      void load();
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      apply(await supportTicketService.withdraw(ticketId));
      setConfirmWithdraw(false);
      toast.success('Ticket zurückgezogen – es wird nichts berechnet.');
    } catch (e: any) {
      toast.error(e?.message || 'Ticket konnte nicht zurückgezogen werden.');
      setConfirmWithdraw(false);
      void load();
    } finally {
      setBusy(false);
    }
  };

  const back = (
    <button
      type="button"
      onClick={onBack}
      className="mb-2 inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" /> Zurück zu deinen Tickets
    </button>
  );

  if (loading && !ticket) {
    return (
      <Card className="shadow-sm border-border">
        <CardContent className="flex min-h-[200px] items-center justify-center" role="status">
          <Loader2 className="h-6 w-6 animate-spin text-[#1967D2]" aria-hidden="true" />
          <span className="sr-only">Ticket wird geladen…</span>
        </CardContent>
      </Card>
    );
  }
  if (failed || !ticket) {
    return (
      <Card className="shadow-sm border-border">
        <CardContent className="flex min-h-[200px] flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
          {back}
          Ticket konnte nicht geladen werden.
          <Button variant="outline" size="sm" onClick={() => void load()}>
            Erneut versuchen
          </Button>
        </CardContent>
      </Card>
    );
  }

  const frequencyLabel = config.frequencies.find((f) => f.code === ticket.frequency)?.label ?? ticket.frequency;
  const when = ticket.conversation_started_at || ticket.occurred_at;

  return (
    <Card className="shadow-sm border-border">
      <CardHeader className="space-y-3">
        {back}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle ref={headingRef} role="heading" aria-level={2} tabIndex={-1} className="text-lg outline-none">
              <span className="font-mono text-muted-foreground">#{ticket.id}</span> {ticket.category_label}
            </CardTitle>
            <SupportTicketStatusBadge status={ticket.status} />
          </div>
          {ticket.can_withdraw && (
            <Button variant="outline" size="sm" onClick={() => setConfirmWithdraw(true)} disabled={busy}>
              <Undo2 className="mr-1.5 h-4 w-4" /> Zurückziehen
            </Button>
          )}
        </div>
        <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
          {billingNote(ticket)}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {ticket.status === 'waiting_for_customer' && (
          <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            Wir haben eine Rückfrage an dich – bitte antworte unten, dann geht es weiter.
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Agent">
            {SUPPORT_TICKET_CHANNEL_LABELS[ticket.channel]}
            {ticket.agent_number ? ` · ${ticket.agent_number}` : ''}
          </Fact>
          <Fact label="Wann">{formatDateTime(when)}</Fact>
          <Fact label="Häufigkeit">{frequencyLabel}</Fact>
          <Fact label="Eingereicht">{formatDateTime(ticket.created_at)}</Fact>
        </div>

        {ticket.conversation_id && (
          <Link
            to={`/conversations?c=${ticket.conversation_id}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1967D2] hover:underline"
          >
            <ExternalLink className="h-4 w-4" /> Betroffenes Gespräch ansehen
          </Link>
        )}

        <div className="space-y-4">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Was ist passiert?</p>
            <p className="whitespace-pre-wrap break-words text-sm text-foreground">{ticket.description}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Was hätte der Agent tun sollen?
            </p>
            <p className="whitespace-pre-wrap break-words text-sm text-foreground">{ticket.expected_behavior}</p>
          </div>
        </div>

        <div className="space-y-3 border-t border-border pt-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Verlauf</p>
          {ticket.messages.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Noch keine Nachrichten. Sobald sich unser Team meldet, steht es hier – und du bekommst eine E-Mail.
            </p>
          ) : (
            <ul className="space-y-2">
              {ticket.messages.map((m) => {
                const mine = m.author_type === 'customer';
                return (
                  <li key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                    <div
                      className={cn(
                        'max-w-[85%] rounded-xl px-3 py-2 text-sm',
                        mine ? 'bg-[#1967D2] text-white' : 'border border-border bg-muted text-foreground',
                      )}
                    >
                      <p className={cn('mb-1 text-[11px]', mine ? 'text-white/80' : 'text-muted-foreground')}>
                        {/* Team-Nachrichten einheitlich — die internen Kontonamen
                            („EcomTask Admin“) sind für Salons bedeutungslos. */}
                        <span className="font-semibold">{mine ? 'Du' : 'Support-Team'}</span>
                        {' · '}
                        {formatDateTime(m.created_at)}
                      </p>
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {ticket.can_reply && (
            <div className="space-y-2">
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                maxLength={config.limits.message_max}
                rows={3}
                disabled={busy}
                placeholder={
                  ticket.status === 'waiting_for_customer'
                    ? 'Deine Antwort auf unsere Rückfrage'
                    : 'Ergänzung oder Nachricht an unser Team'
                }
                aria-label="Nachricht an das Support-Team"
              />
              <CharCounter value={reply} max={config.limits.message_max} />
              <div className="flex justify-end">
                <Button
                  onClick={() => void sendReply()}
                  disabled={!reply.trim() || busy}
                  className="bg-[#1967D2] hover:bg-[#1558b0]"
                >
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Senden
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>

      <Dialog open={confirmWithdraw} onOpenChange={(open) => !busy && setConfirmWithdraw(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ticket #{ticket.id} zurückziehen?</DialogTitle>
            <DialogDescription>
              Das Ticket wird geschlossen und nicht berechnet. Das geht nur, solange unser Team noch nicht daran
              arbeitet.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmWithdraw(false)} disabled={busy}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={() => void withdraw()} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}
              Zurückziehen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default SupportTicketView;
